import { useEffect, useState } from 'react';
import { useMounted } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle } from 'react-icons/fi';

import { Flex, Loader, Text } from '@highlight-cp/core-ui';

interface AutoSaveStatusProps {
  className?: string;
  isSaving: boolean;
  /** When the work was last stored, shown in place of the last save seen here. */
  savedAt?: Date;
  showTimestamp?: boolean;
}

/**
 * The status sits beside a row of figures wherever it appears — a header, a control bar — so it is
 * set in the small, medium-weight type those read in rather than on the body scale it would take
 * on a line of its own (EPD-1562). A `savedAt` only shows once mounted, since the server would
 * format it in its own time zone.
 */
const AutoSaveStatus = ({
  isSaving,
  className,
  savedAt,
  showTimestamp = true,
}: AutoSaveStatusProps) => {
  const { t } = useTranslation();
  const isMounted = useMounted();
  const [saved, setSaved] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!isSaving && saved) {
      setLastSavedAt(new Date());
    } else if (isSaving) {
      setSaved(true);
    }
  }, [isSaving, saved]);

  const shownSavedAt = (isMounted && savedAt) || lastSavedAt;

  if (!isSaving && !shownSavedAt) {
    return null;
  }

  return (
    <Flex align="center" className={className} gap={4}>
      {shownSavedAt && showTimestamp && (
        <Text c="dimmed" fw={600} size="xs">
          {t('Saved: {{time}}', { time: shownSavedAt.toLocaleTimeString() })}
        </Text>
      )}
      {isSaving ? (
        <Loader size={16} />
      ) : (
        <FiCheckCircle color="var(--mantine-color-success)" size={16} />
      )}
    </Flex>
  );
};

export default AutoSaveStatus;
